import React, {useEffect, useState} from "react";
import { generatePath, useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { FlashMessage } from "../../assets/utils";
import ROUTES from "../../../route";

const BookForm = ({book = null}) => {
    const navigate = useNavigate();
    const [flashMessage, setFlashMessage] = useState(null);
    const [errors, setErrors] = useState({
        date_of_publication: undefined
    });
    const [genres, setGenres] = useState([]);
    const [authors, setAuthors] = useState([]);
    const [searchAuthor, setSearchAuthor] = useState([]);
    const [author, setAuthor] = useState([]);
    const [authorId, setAuthorId] = useState(null);
    const [title, setTitle] = useState(book?.book.title ?? '');
    const [genresSelected, setGenreSelected] = useState(book?.book.genres ?? [])
    const [description, setDescription] = useState(book?.book.description ?? '')
    const [dateOfPublication, setDateOfPublication] = useState(book?.book.date_of_publication ?? '')

    useEffect(() => {
        const fetchGenres = async () => {
            try {
                const response = await api.get('/getGenres');
                setGenres(response.data.genres);
            } catch (error) {
                setFlashMessage('Failed to load genres. Please refresh the page.');
                console.error('Failed to fetch genres', error);
            }
        };
        fetchGenres();

        const fetchAuthors = async () => {
            try {
                const response = await api.get('/getAuthors');
                setAuthors(response.data.authors);
            } catch (error) {
                setFlashMessage('Failed to load authors. Please refresh the page.');
                console.error('Failed to fetch authors', error);
            }
        }
        if (book?.book.author){
            setAuthor(book.book.author);
            setAuthorId(book.book.author.id);
        }
        fetchAuthors();
    }, []);

    const findAuthors = (event) => {
        setAuthorId(null);
        const searchTerm = event.target.value.toLowerCase();
        const filteredAuthors = authors.filter(author =>
            author.name.toLowerCase().includes(searchTerm)
        );
        setSearchAuthor(filteredAuthors);
        setAuthor(event.target.value);
    }

    const updateAuthor = (authorId) => {
        const findAuthor = authors.find(author => author.id === authorId);
        setAuthor(findAuthor);
        setAuthorId(findAuthor.id);
        setSearchAuthor([]);
    }


    const handleSubmitForm = async (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        // Only append a real id: FormData stringifies null to "null", which is
        // truthy, so appending it unconditionally made the "create the author
        // first" branch below unreachable and posted author_id="null".
        if (authorId) {
            formData.append('author_id', authorId);
        }

        const storeData = async () => {
            let genres = [];

            for (let [key, value] of formData.entries()) {
                if (key === 'genres') {
                    genres.push(parseInt(value));
                }
            }

            let newFormData = new FormData();

            for (let [key, value] of formData.entries()) {
                if (key !== 'genres' && key !== 'date_of_publication') {
                    newFormData.append(key, value);
                }
            }

            genres.forEach(genreId => newFormData.append('genres[]', genreId));

            const dateOfPublication = formData.get('date_of_publication');
            if (dateOfPublication) {
                const date = new Date(dateOfPublication);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                const formattedDate = `${day}/${month}/${year}`; // Correct format
                newFormData.append('date_of_publication', formattedDate);
            }

            setErrors({}); // Clear previous errors
            try {
                let url = null;
                if (book !== null) {
                    url = `/updateBook/${book.book.id}`;
                } else {
                    url = '/storeBook';
                }
                const response = await api.post(url, newFormData);
                const bookId = response.data.id;
                setFlashMessage(book !== null ? 'Book updated successfully!' : 'Book added to your library!');
                setTimeout(() => {
                    navigate(generatePath(ROUTES.BOOK_SHOW, { id: bookId }));
                }, 1500);
            } catch (error) {
                if (error.response && error.response.data && error.response.data.errors) {
                    setErrors(error.response.data.errors);
                } else {
                    const message = error.response?.data?.message || 'Something went wrong. Please try again.';
                    setFlashMessage(message);
                    console.error('An unexpected error occurred:', error);
                }
            }
        }

        if (!formData.get('author_id')) {
            try {
                const response = await api.post('/storeAuthor', {
                    author: formData.get('author')
                })
                setAuthorId(response.data.id);
                formData.append('author_id', response.data.id);
                await storeData();
            } catch (error) {
                if (error.response && error.response.data && error.response.data.errors) {
                    setErrors(error.response.data.errors);
                } else {
                    const message = error.response?.data?.message || 'Something went wrong. Please try again.';
                    setFlashMessage(message);
                    console.error('An unexpected error occurred:', error);
                }
            }
        } else {
            await storeData();
        }
    }

    const handleTitleChange = (event) => {
        setTitle(event.target.value);
    }

    const handleGenreChange = (event) => {
        const newGenre = genres.find(genre => genre.id === parseInt(event.target.value, 10));
        setGenreSelected(prevState => {
            if (prevState.some(g => g.id === newGenre.id)) {
                return prevState.filter(g => g.id !== newGenre.id);
            } else {
                return [...prevState, newGenre];
            }
        });
    }

    const handleDescriptionChange = (event) => {
        setDescription(event.target.value);
    }

    const HandleDateOfPublicationChange = (event) => {
        setDateOfPublication(event.target.value);
    }

    return (
        <>
        {flashMessage && (
            <FlashMessage message={flashMessage} duration={1500} onClose={() => setFlashMessage(null)} />
        )}
        <form
            onSubmit={handleSubmitForm}
            className="space-y-4"
        >
            <div>
                <label htmlFor="title" className="block text-gray-700 font-bold mb-2">
                    Title
                </label>
                <input
                    type="text"
                    id="title"
                    name="title"
                    onChange={handleTitleChange}
                    value={title}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                />
                {errors.title && <div className="text-red-500 text-sm mt-1">{errors.title[0]}</div>}
            </div>

            {!book && (
                <div>
                    <label htmlFor="author" className="block text-gray-700 font-bold mb-2">
                        Author
                    </label>
                    <input
                        type="text"
                        id="author"
                        name="author"
                        value={author.name}
                        onChange={findAuthors}
                        data-author-id={authorId ?? ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                    />
                    {searchAuthor.length > 0 &&
                        <div>
                            <ul className="mt-2">
                                {searchAuthor.map((author) => (
                                    <li onClick={() => updateAuthor(author.id)} key={author.id}>{author.name}</li>
                                ))}
                            </ul>
                        </div>
                    }
                    {(errors.author || errors.author_id) && <div className="text-red-500 text-sm mt-1">{(errors.author || errors.author_id)[0]}</div>}
                </div>
            )}



            <div className="h-60 overflow-y-auto">
                <div>
                    <label htmlFor="genres" className="block text-gray-700 font-bold mb-2">
                        Genres
                    </label>
                    <div className="space-y-2 h-40 overflow-y-auto">
                        {genres && genres.sort((a, b) => a.name.localeCompare(b.name)).map((genre, index) => (
                            <div key={index} className="flex items-center">
                                <input
                                    type="checkbox"
                                    id={`genre-${genre.id}`}
                                    name="genres"
                                    value={genre.id}
                                    className="mr-2 h-4 w-4"
                                    onChange={handleGenreChange}
                                    checked={genresSelected.some(g => g.id === genre.id)}
                                />
                                <label htmlFor={`genre-${genre.id}`}>{genre.name}</label>
                            </div>
                        ))}
                        {errors.genres && <div className="text-red-500 text-sm mt-1">{errors.genres[0]}</div>}
                    </div>
                </div>
            </div>


            <div>
                <label
                    htmlFor="description"
                    className="block text-gray-700 font-bold mb-2"
                >
                    Description
                </label>
                <textarea
                    id="description"
                    name="description"
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                    value={description}
                    onChange={handleDescriptionChange}
                ></textarea>
                {errors.description && <div className="text-red-500 text-sm mt-1">{errors.description[0]}</div>}
            </div>

            <div>
                <label
                    htmlFor="date_of_publication"
                    className="block text-gray-700 font-bold mb-2"
                >
                    Date of Publication
                </label>
                <input
                    type="date"
                    id="date_of_publication"
                    name="date_of_publication"
                    value={dateOfPublication.split(' ')[0]}
                    onChange={HandleDateOfPublicationChange}
                />
                {errors.date_of_publication && <div className="text-red-500 text-sm mt-1">{errors.date_of_publication[0]}</div>}
            </div>

            <div>
                <label htmlFor="picture" className="block text-gray-700 font-bold mb-2">
                    Picture
                </label>
                <input
                    type="file"
                    id="picture"
                    name="picture"
                    className="w-full"
                />
                {errors.picture && <div className="text-red-500 text-sm mt-1">{errors.picture[0]}</div>}
            </div>

            <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring focus:ring-blue-200"
            >
                Submit
            </button>
        </form>
        </>
    )
}

export default BookForm;